import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, provide } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import type { BuyboxRegularOffer, UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';

import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { qtvProvidersByType } from '@/features/buybox/helpers/quality-tv-data';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { MonetizationType } from '@/@types/graphql-types';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegularListBuyBox',
  props: {
    regularOffers: null,
    allOffers: null,
    filter: null,
    title: null,
    additionalContexts: null,
    upcomingReleases: null,
    isInWatchlist: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    isEpisodeBuybox: { type: Boolean },
    hasVODOffers: { type: Boolean }
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	regularOffers: BuyboxRegularOffer[];
	allOffers: TitleOfferFragment[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	upcomingReleases: UpcomingReleaseBuybox[];
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
	hasVODOffers: boolean;
};

const ListUpcoming = () => import('@/features/buybox/components/list/ListUpcoming.vue');
const ListOffer = () => import('@/features/buybox/components/list/ListOffer.vue');
const NoOffers = () => import('@/features/buybox/components/list/NoOffers.vue');
const NotifyMe = () => import('@/features/buybox/components/list/NotifyMe.vue');
const PromotionPlacement = () => import('@/features/buybox/components/list/PromotionPlacement.vue');





const { language, country } = useLanguageStore();

const buyboxContainer = ref<HTMLElement | null>(null);
provide('offerLocation', OfferLocation.REGULAR_BUYBOX);

const { promotionOffer, inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.allOffers,
});

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const hasFreeTrialOffers = computed(() => props?.regularOffers.length > 0 || inPromotionCatalogue.value);

const showNoOffers = computed(() => props.regularOffers.length === 0);

const showNotifyMeNew = computed(() => {
	return props.filter === BuyBoxFilterOptions.FREE ? hasFreeTrialOffers.value : !showNoOffers.value;
});

const hasOnlyUpcomingOffers = computed(() => props.upcomingReleases.length > 0 && showNoOffers.value);

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Quality TV

const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });

const includeTVODQTVonRentOrBuy = computed(() => {
	// Include TVOD QTV (which acts as a 'freeTrial' offer) also on Rent and Buy filters. Other free trial offers are not included.
	return (
		catalogueItem.value?.placement === 'qtv' &&
		qtvProvidersByType['TVOD'].includes(catalogueItem.value.shortName) &&
		[BuyBoxFilterOptions.BUY, BuyBoxFilterOptions.RENT].includes(props.filter)
	);
});

// *HACK* Single day exception for this one title while a push notification goes out *HACK*
const isRunningManDE = computed(() => props.title.id === 'tm1542567' && country.value === 'DE');

const { hasActiveJwtvCampaign } = useJWTVCampaign();
const _allOffers = computed<BuyboxRegularOffer[]>(() => {
	let offers: BuyboxRegularOffer[] = [...props.regularOffers];
	// Add promotion offer to the list of offers
	if (
		!showNoOffers.value &&
		([BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) ||
			includeTVODQTVonRentOrBuy.value) &&
		!!promotionOffer.value
	) {
		// OVER_PROVIDER
		if (isOverProviderTitle.value) {
			offers = [
				{ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer,
				// and we filter out the offers that are of the same provider as the free trial
				...offers.filter(offer => offer.package.shortName !== promotionOffer.value?.package.shortName),
			];
		}

		// *HACK* Single day exception for this one title while a push notification goes out *HACK*
		else if (isRunningManDE.value) {}
		// prettier-ignore

		// OVER_PROVIDER
		else if (offers.length > 2) {
			offers.splice(2, 0, { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		} else {
			offers.push({ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		}
	}

	if (hasActiveJwtvCampaign.value) {
		const [rentalOffers, restOffers] = offers.reduce<[BuyboxRegularOffer[], BuyboxRegularOffer[]]>(
			(acc, offer) => {
				if (offer.monetizationType === MonetizationType.Rent) {
					acc[0].push(offer);
				} else {
					acc[1].push(offer);
				}
				return acc;
			},
			[[], []]
		);

		offers = [...rentalOffers, ...restOffers];
	}

	return offers.filter(Boolean);
});

/**
 * ALL: Promotions shown after NoOffers component instead (line 89)
 * PHYSICAL: Physical shouldn't show streaming promotions
 * CINEMA: Theater shouldn't show streaming promotions
 */
const showTopPositionPromotionPlacement = computed(
	() =>
		showNoOffers.value &&
		props.filter !== BuyBoxFilterOptions.ALL &&
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA
);

/**
 * Filter exclusions:
 * PHYSICAL: Disc tab shouldn't render anything unless there are actual physcial media offers
 * CINEMA: same as above
 *
 * Special handling:
 * FREE filter: Only show if not in promotion catalogue (promotions show instead)
 * Other filters: Only show if no upcoming releases (upcoming releases show instead)
 */
const showTopPositionNoOffers = computed(
	() =>
		props.filter !== BuyBoxFilterOptions.PHYSICAL &&
		props.filter !== BuyBoxFilterOptions.CINEMA &&
		(props.filter === BuyBoxFilterOptions.FREE
			? showNoOffers.value && !inPromotionCatalogue.value
			: showNoOffers.value && props.upcomingReleases.length === 0)
);

// Split offers into visible and hidden for correct ordering
const visibleOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return _allOffers.value;
	}
	// Filter out cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return false;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return false;
		return true;
	});
});

const hiddenOffers = computed(() => {
	if (props.filter !== BuyBoxFilterOptions.ALL || !props.hasVODOffers) {
		return [];
	}
	// Only cinema and physical offers
	return _allOffers.value.filter(offer => {
		if (offer.monetizationType === 'CINEMA') return true;
		if (offer.presentationType && ['DVD', 'BLURAY', 'BLURAY_4K'].includes(offer.presentationType)) return true;
		return false;
	});
});

return { __sfc: true,ListUpcoming, ListOffer, NoOffers, NotifyMe, PromotionPlacement, props, language, country, buyboxContainer, promotionOffer, inPromotionCatalogue, catalogueItem, showFreeTrial, hasFreeTrialOffers, showNoOffers, showNotifyMeNew, hasOnlyUpcomingOffers, qualityTvTrackingContexts, isOverProviderTitle, includeTVODQTVonRentOrBuy, isRunningManDE, hasActiveJwtvCampaign, _allOffers, showTopPositionPromotionPlacement, showTopPositionNoOffers, visibleOffers, hiddenOffers }
}

})